import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, Info, Lock } from "lucide-react";
import { ApiError } from "@am/shared";
import { toast } from "sonner";
import { useApiClient } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import { useMe } from "../hooks/useMe";
import { PageHeader } from "../components/PageHeader";
import { Tabs, TabPane } from "../components/Tabs";
import { ColorField } from "../components/ColorField";

type TabKey = "widget" | "account" | "llmkey";

export function SettingsPage() {
  const { t } = useI18n();
  const [active, setActive] = useState<TabKey>("widget");

  return (
    <div className="page is-active">
      <PageHeader titleKey="settings_title" subtitleKey="settings_subtitle" />
      <Tabs
        active={active}
        onChange={(k) => setActive(k as TabKey)}
        tabs={[
          { key: "widget", label: t("tab_widget") },
          { key: "account", label: t("tab_account") },
          { key: "llmkey", label: t("tab_llm_key") },
        ]}
      >
        <TabPane active={active} when="widget">
          <WidgetTab />
        </TabPane>
        <TabPane active={active} when="account">
          <AccountTab />
        </TabPane>
        <TabPane active={active} when="llmkey">
          <LlmKeyTab />
        </TabPane>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Widget tab — customize bot name, colors, sizes, language, business info.
// ---------------------------------------------------------------------------

function WidgetTab() {
  const api = useApiClient();
  const { t } = useI18n();
  const { me, loading, refresh } = useMe();

  // Local form state — initialised once me is loaded. Keeping it as a single
  // object mirrors the original page's flat form layout.
  const [form, setForm] = useState({
    bot_name: "",
    position: "bottom-right",
    primary_color: "#FF6B1A",
    secondary_color: "#E04E00",
    welcome_message: "",
    logo_url: "",
    widget_icon_url: "",
    widget_size: "medium",
    language: "fr",
    placeholder_text: "",
    powered_by: true,
    business_info: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ msg: string; kind: "ok" | "err" } | null>(null);

  useEffect(() => {
    if (!me) return;
    const wc = me.widget_config;
    setForm({
      bot_name: wc.bot_name ?? "",
      position: wc.position ?? "bottom-right",
      primary_color: wc.primary_color ?? "#FF6B1A",
      secondary_color: wc.secondary_color ?? "#E04E00",
      welcome_message: wc.welcome_message ?? "",
      logo_url: wc.logo_url ?? "",
      widget_icon_url: wc.widget_icon_url ?? "",
      widget_size: wc.widget_size ?? "medium",
      language: wc.language ?? "fr",
      placeholder_text: wc.placeholder_text ?? "",
      powered_by: wc.powered_by !== false,
      business_info: me.business_info ?? "",
    });
  }, [me]);

  const canCustomize = me?.can_customize_widget !== false;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setStatus({ msg: t("saving"), kind: "ok" });
    try {
      await api("/tenant/api/settings", {
        method: "PATCH",
        body: {
          bot_name: form.bot_name.trim() || null,
          position: form.position,
          primary_color: form.primary_color,
          secondary_color: form.secondary_color,
          welcome_message: form.welcome_message.trim() || null,
          logo_url: form.logo_url.trim() || null,
          widget_icon_url: form.widget_icon_url.trim() || null,
          widget_size: form.widget_size,
          language: form.language,
          placeholder_text: form.placeholder_text.trim() || null,
          business_info: form.business_info.trim() || null,
        },
      });
      setStatus({ msg: t("saved"), kind: "ok" });
      toast.success(t("saved"));
      await refresh();
      window.setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("error_generic");
      setStatus({ msg, kind: "err" });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading || !me) return <div style={{ padding: 24, color: "var(--text-muted)" }}>…</div>;

  return (
    <>
      {!canCustomize && (
        <div className="alert alert-warn" style={{ marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 8 }}>
          <Lock size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          {t("widget_locked_notice")}
        </div>
      )}
      <div
        style={{
          opacity: canCustomize ? 1 : 0.45,
          pointerEvents: canCustomize ? undefined : "none",
        }}
      >
        <form onSubmit={onSubmit}>
          <div className="settings-section">
            <h3>{t("widget_appearance")}</h3>
            <div className="form-grid">
              <div className="field">
                <label>{t("bot_name")}</label>
                <input
                  type="text"
                  value={form.bot_name}
                  onChange={(e) => setForm((f) => ({ ...f, bot_name: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>{t("position")}</label>
                <select
                  value={form.position}
                  onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                >
                  <option value="bottom-right">{t("pos_right")}</option>
                  <option value="bottom-left">{t("pos_left")}</option>
                </select>
              </div>
              <ColorField
                label={t("primary_color")}
                value={form.primary_color}
                onChange={(v) => setForm((f) => ({ ...f, primary_color: v }))}
                placeholder="#FF6B1A"
              />
              <ColorField
                label={t("secondary_color")}
                value={form.secondary_color}
                onChange={(v) => setForm((f) => ({ ...f, secondary_color: v }))}
                placeholder="#E04E00"
              />
              <div className="field form-full">
                <label>{t("welcome_msg")}</label>
                <input
                  type="text"
                  value={form.welcome_message}
                  onChange={(e) => setForm((f) => ({ ...f, welcome_message: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>{t("logo_url")}</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={form.logo_url}
                  onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>{t("widget_icon_url")}</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={form.widget_icon_url}
                  onChange={(e) => setForm((f) => ({ ...f, widget_icon_url: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>{t("widget_size")}</label>
                <select
                  value={form.widget_size}
                  onChange={(e) => setForm((f) => ({ ...f, widget_size: e.target.value }))}
                >
                  <option value="small">{t("size_small")}</option>
                  <option value="medium">{t("size_medium")}</option>
                  <option value="large">{t("size_large")}</option>
                </select>
              </div>
              <div className="field">
                <label>{t("widget_language")}</label>
                <select
                  value={form.language}
                  onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              <div className="field">
                <label>{t("placeholder_text")}</label>
                <input
                  type="text"
                  value={form.placeholder_text}
                  onChange={(e) => setForm((f) => ({ ...f, placeholder_text: e.target.value }))}
                />
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <div className="toggle-row">
                <div>
                  <div className="toggle-label">{t("powered_by_label")}</div>
                  <div className="toggle-desc">{t("powered_by_desc")}</div>
                </div>
                <input
                  type="checkbox"
                  className="toggle"
                  checked={form.powered_by}
                  disabled
                  readOnly
                />
              </div>
              <div className="powered-by-note">
                <Info size={14} />
                <span>{t("powered_by_readonly_note")}</span>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>{t("business_info_title")}</h3>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>{t("business_info_label")}</label>
              <textarea
                rows={6}
                placeholder={"- Nom : Mon Magasin\n- Adresse : 15 rue de la Paix\n- Tél : +213 XXX XXXXXX\n- Horaires : Lun-Sam 9h-18h"}
                value={form.business_info}
                onChange={(e) => setForm((f) => ({ ...f, business_info: e.target.value }))}
              />
            </div>
          </div>

          <div className="btn-row">
            <button type="submit" className="btn-sm btn-orange" disabled={submitting || !canCustomize}>
              {t("save_btn")}
            </button>
            {status && (
              <span
                style={{
                  fontSize: 12,
                  color: status.kind === "err" ? "var(--red)" : "var(--green)",
                }}
              >
                {status.msg}
              </span>
            )}
          </div>
        </form>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Account tab — change password.
// ---------------------------------------------------------------------------

function AccountTab() {
  const api = useApiClient();
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [alert, setAlert] = useState<{ kind: "alert-error" | "alert-success"; msg: string } | null>(null);

  useEffect(() => {
    if (!alert) return;
    const t = window.setTimeout(() => setAlert(null), 4000);
    return () => window.clearTimeout(t);
  }, [alert]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (next !== confirm) {
      setAlert({ kind: "alert-error", msg: t("pwd_mismatch") });
      return;
    }
    if (next.length < 8) {
      setAlert({ kind: "alert-error", msg: t("pwd_too_short") });
      return;
    }
    try {
      await api("/tenant/api/change-password", {
        method: "POST",
        body: { current_password: current, new_password: next },
      });
      setAlert({ kind: "alert-success", msg: t("pwd_success") });
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success(t("pwd_success"));
    } catch (err) {
      const msg =
        err instanceof ApiError && /Incorrect/i.test(err.message)
          ? t("pwd_wrong")
          : err instanceof Error
            ? err.message
            : t("error_generic");
      setAlert({ kind: "alert-error", msg });
    }
  }

  return (
    <div className="settings-section">
      <h3>{t("change_password")}</h3>
      <form onSubmit={onSubmit}>
        <div className="form-grid">
          <div className="field form-full">
            <label>{t("current_password")}</label>
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="field">
            <label>{t("new_password")}</label>
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </div>
          <div className="field">
            <label>{t("confirm_password")}</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
        </div>
        {alert && (
          <div className={"alert " + alert.kind} style={{ marginTop: 8 }}>
            {alert.msg}
          </div>
        )}
        <div className="btn-row" style={{ marginTop: 14 }}>
          <button type="submit" className="btn-sm btn-orange">
            {t("save_btn")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LLM Key tab — set / rotate the OpenAI API key.
// ---------------------------------------------------------------------------

function LlmKeyTab() {
  const api = useApiClient();
  const { t } = useI18n();
  const { me, refresh } = useMe();
  const [key, setKey] = useState("");
  const [alert, setAlert] = useState<{ kind: "alert-error" | "alert-success"; msg: string } | null>(null);

  const configured = me?.llm_key_configured ?? false;
  const masked = me?.llm_key_masked ?? "";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = key.trim();
    if (!trimmed) {
      setAlert({ kind: "alert-error", msg: "Clé vide." });
      return;
    }
    setAlert(null);
    try {
      await api("/tenant/api/llm-key", {
        method: "POST",
        body: { api_key: trimmed },
      });
      setKey("");
      await refresh();
      toast.success(t("llm_key_saved"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("error_generic");
      setAlert({ kind: "alert-error", msg });
    }
  }

  return (
    <div className="settings-section">
      <h3>{t("llm_key_title")}</h3>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
        {t("llm_key_desc")}
      </p>
      <div style={{ marginBottom: 16 }}>
        <span className={"badge " + (configured ? "badge-green" : "badge-yellow")}>
          {configured ? t("llm_key_ok") : t("llm_key_not_set")}
        </span>
        {configured && (
          <span className="mono" style={{ marginLeft: 10 }}>
            {masked}
          </span>
        )}
      </div>
      <div className="alert alert-warn" style={{ display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={14} style={{ flexShrink: 0 }} /> {t("llm_key_warning")}</div>
      <form onSubmit={onSubmit}>
        <div className="field" style={{ marginTop: 16 }}>
          <label>{t("llm_key_new")}</label>
          <input
            type="password"
            autoComplete="off"
            placeholder="sk-..."
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
        </div>
        {alert && (
          <div className={"alert " + alert.kind} style={{ marginTop: 8 }}>
            {alert.msg}
          </div>
        )}
        <div className="btn-row" style={{ marginTop: 14 }}>
          <button type="submit" className="btn-sm btn-orange">
            {t("llm_key_save")}
          </button>
        </div>
      </form>
    </div>
  );
}
